import { BryntumSchedulerPro } from '@bryntum/schedulerpro-react';
import { useEffect, useRef } from 'react';
import {
  ComplexLossFilters,
  ComplexLossDataResponse,
  endDate,
  startDate,
  ComplexLossEventSchema,
  defaultTimeZone,
} from '@dispatch/shared/types';
import { useNavigate } from 'react-router-dom';
import { PaginatedQueryResult, useGetTimeZones } from '@dispatch/shared/hooks';
import { useIsFetching } from 'react-query';
import React from 'react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { useLocalStorage } from 'react-use';
import { useCurrentUser } from '@dispatch/web/auth';

const endDateSpan = (date: Date, offset: number) => {
  const endDateSpan = new Date(date);
  endDateSpan.setDate(endDateSpan.getDate() + offset);

  return endDateSpan;
};

export interface IComplexLossTreeViewProps {
  complexLossData: ComplexLossDataResponse;
  complexLossQuery: PaginatedQueryResult<any, unknown>;
  apiFilters: ComplexLossFilters;
  setApiFilters: React.Dispatch<React.SetStateAction<ComplexLossFilters>>;
}

export const ComplexLossTreeView = ({
  complexLossData,
  complexLossQuery,
  apiFilters,
  setApiFilters,
}: IComplexLossTreeViewProps) => {
  const navigate = useNavigate();
  const isFetching = useIsFetching(['complex-loss-data']);
  const [isLoading, setIsLoading] = React.useState(false);
  const [timeZone, setTimeZone] = useLocalStorage('timeZone', defaultTimeZone);
  const [events, setEvents] = React.useState<ComplexLossEventSchema[]>(
    complexLossData?.events || []
  );

  const { tenantId } = useCurrentUser();
  const { timeZones } = useGetTimeZones(tenantId || '');

  useEffect(() => {
    const events = complexLossData?.events?.map((event) => ({
      ...event,
      startDate: utcToZonedTime(
        event.startDate,
        timeZone || defaultTimeZone
      ).toISOString(),
      endDate: utcToZonedTime(
        event.endDate,
        timeZone || defaultTimeZone
      ).toISOString(),
    }));

    setEvents(events);
  }, [complexLossData?.events, timeZone]);

  const complexLossSchedulerRef = useRef<BryntumSchedulerPro>(null);
  const complexLossSchedulerInstance = () =>
    complexLossSchedulerRef.current?.instance;

  useEffect(() => {
    const isLoading = isFetching > 0 || complexLossQuery?.status === 'loading';

    setIsLoading(isLoading);
  }, [complexLossQuery?.status, isFetching]);

  useEffect(() => {
    if (isLoading) {
      complexLossSchedulerInstance()?.maskBody('Loading data...');
    } else {
      complexLossSchedulerInstance()?.unmaskBody();
    }
  }, [isLoading]);

  const shiftPrevious = () => {
    const _startDate = apiFilters.startDate;
    const _endDate = apiFilters.endDate;

    _startDate.setDate(_startDate.getDate() - 7);
    _endDate.setDate(_endDate.getDate() - 7);

    complexLossSchedulerInstance()?.setTimeSpan(
      apiFilters.startDate,
      endDateSpan(apiFilters.endDate, 0)
    );

    setApiFilters({
      ...apiFilters,
      startDate: _startDate,
      endDate: _endDate,
    });
  };

  const shiftToday = () => {
    const _startDate = startDate();
    const _endDate = endDate();

    complexLossSchedulerInstance()?.setTimeSpan(
      _startDate,
      endDateSpan(_endDate, -1)
    );

    setApiFilters({
      ...apiFilters,
      startDate: _startDate,
      endDate: _endDate,
    });
  };

  const shiftNext = () => {
    const _startDate = apiFilters.startDate;
    const _endDate = apiFilters.endDate;

    _startDate.setDate(_startDate.getDate() + 7);
    _endDate.setDate(_endDate.getDate() + 7);

    complexLossSchedulerInstance()?.setTimeSpan(
      apiFilters.startDate,
      endDateSpan(apiFilters.endDate, -1)
    );

    setApiFilters({
      ...apiFilters,
      startDate: _startDate,
      endDate: _endDate,
    });
  };

  const paginationText = () => {
    const dataCount =
      complexLossQuery?.currentPageNumber * apiFilters?.pageSize;
    if (complexLossQuery?.hasNextPage) {
      return `${dataCount + 1} - 
        ${dataCount + apiFilters?.pageSize} of more than 
        ${dataCount + apiFilters?.pageSize}`;
    } else {
      return `${dataCount + 1} - ${
        dataCount + complexLossQuery?.data?.resources?.length
      }`;
    }
  };

  const eventToolTip = (data: any) => {
    if (data.eventRecord.eventColor === 'green') {
      return `
      <div style="margin: 8px 0 0 0">
        ${format(data.startDate, 'MM/dd/yyyy hh:mm a')}
      </div>`;
    }

    return `
      <div style="margin: 8px 0 0 0">
        ${format(data.startDate, 'MM/dd/yyyy hh:mm a')} - ${format(
      data.endDate,
      'MM/dd/yyyy hh:mm a'
    )}
      </div>
    `;
  };

  return (
    <BryntumSchedulerPro
      ref={complexLossSchedulerRef}
      events={events}
      assignments={complexLossData?.assignments}
      resources={complexLossData?.resources}
      readOnly={true}
      startDate={apiFilters.startDate}
      endDate={endDateSpan(apiFilters.endDate, -1)}
      //config
      weekStartDay={1}
      presets={[
        {
          base: 'hourAndDay',
        },
        {
          base: 'weekAndDayLetter',
        },
      ]}
      viewPreset={'weekAndDayLetter'}
      zoomKeepsOriginalTimespan={true}
      height={'70vh'}
      columns={[
        {
          text: 'Id',
          field: 'id',
          width: 50,
          hidden: true,
          sortable: false,
        },
        {
          type: 'tree',
          text: 'Name',
          field: 'name',
          width: 150,
          sortable: false,
        },
        {
          text: 'Team',
          field: 'team',
          width: 150,
          sortable: false,
          // this is required as bryntum won't display the value if the parent doesn't have the field
          renderer: ({ record }: { record: any }) => {
            return record?.data?.team;
          },
        },
      ]}
      // features
      treeFeature={true}
      taskEditFeature={false}
      eventEditFeature={false}
      eventTooltipFeature={{
        template: (data: any) => {
          return `
            <div>${data.eventRecord.name}</div>
              ${eventToolTip(data)}
            `;
        },
      }}
      // header bar
      tbar={[
        {
          type: 'buttonGroup',
          items: [
            {
              icon: 'b-icon b-icon-previous',
              onAction: () => {
                shiftPrevious();
                complexLossQuery?.goToFirstPage &&
                  complexLossQuery.goToFirstPage();
              },
              disabled: isLoading,
            },
            {
              type: 'button',
              text: 'Today',
              onAction: () => {
                shiftToday();
                complexLossQuery?.goToFirstPage &&
                  complexLossQuery.goToFirstPage();
              },
              disabled: isLoading,
            },
            {
              icon: 'b-icon b-icon-next',
              onAction: () => {
                shiftNext();
                complexLossQuery?.goToFirstPage &&
                  complexLossQuery.goToFirstPage();
              },
              disabled: isLoading,
            },
          ],
        },
        '->',
        {
          type: 'combo',
          editable: false,
          items: timeZones,
          value: timeZone,
          displayField: 'title',
          onChange: ({ value }: { value: string }) => {
            setTimeZone(value);
          },
          disabled: isLoading,
        },
      ]}
      bbar={[
        '->',
        {
          type: 'buttonGroup',
          items: [
            {
              icon: 'b-icon b-icon-previous',
              disabled: complexLossQuery?.currentPageNumber === 0 || isLoading,
              onAction: complexLossQuery?.getPreviousPage,
            },
            {
              text: paginationText(),
              disabled: true,
            },
            {
              icon: 'b-icon b-icon-next',
              disabled: !complexLossQuery?.hasNextPage || isLoading,
              onAction: complexLossQuery?.getNextPage,
            },
          ],
        },
      ]}
      //listeners
      onEventDblClick={(event: any) => {
        const taskId = event?.eventRecord.id;

        if (taskId) {
          navigate(`/complex-loss/${taskId}`);
        }
      }}
    />
  );
};
